import { apiClient } from './client';
import type { Role } from './users';

export type BillingMode = 'tenant_pays' | 'participant_pays';

/** How a recording starts (ROLES-AND-GO). */
export type StartMode = 'single' | 'button' | 'voice';

export interface TenantContact {
  contact_name: string | null;
  contact_email: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface Tenant extends TenantContact {
  id: string;
  slug: string;
  name: string;
  billing_mode: BillingMode;
  is_active: boolean;
  /**
   * Org-level MFA policy. When true, every active tenant_admin of
   * this tenant must enroll TOTP (RequireAuth gate forces /setup-2fa
   * until they do). When false, the per-user Profile toggle is back
   * in effect. ROADMAP #18 phase 2.
   */
  mfa_required: boolean;
  /**
   * Email-bypass participant enrollment. When true, the tenant_admin
   * Participants page adds participants directly from a form (name +
   * optional email) instead of sending an email invitation — for live
   * demos/sessions. Superadmin-only to change.
   */
  direct_participant_entry: boolean;
  /**
   * Minimum acceptable sensor sampling rate as % of target (EEG 256 Hz,
   * hand IMU 50 Hz) — ROADMAP CAPTURE-RATE-THRESHOLDS. The capture-app
   * makes the operator acknowledge before/while recording below these.
   * 0 disables the gate. Tenant-admin editable.
   */
  min_eeg_rate_pct: number;
  min_imu_rate_pct: number;
  /**
   * Metric keys hidden from the Session-metric scorecard for this tenant
   * (tenant-admin controlled, ROADMAP SCORECARD-METRIC-VISIBILITY).
   * Default-show: a key absent here is visible. Filters both the
   * tenant-admin drill-down and the participant's own scorecard.
   */
  scorecard_hidden_metrics: string[];
  /** Compare-view availability (COMPARE-CONFIG). `compare_enabled_tenant`
   *  gates the Individual/Compare toggle on the tenant dashboard;
   *  `compare_enabled_participant` gates a self-vs-cohort Compare on the
   *  participant dashboard. Tenant-admin controlled. */
  compare_enabled_tenant: boolean;
  compare_enabled_participant: boolean;
  /** The AI Coach may read coaching debrief transcripts (redacted) to pull
   *  out the coach's points. Off by default. */
  ai_coach_use_debrief?: boolean;
  /** ROLES-AND-GO: the capture app asks for a role on every recording. */
  session_roles_enabled?: boolean;
  /** How a recording starts: one Start, Ready + Go button (simulation), or
   *  Ready + a spoken phrase (operating room). */
  start_mode?: StartMode;
  /** The phrase the phone listens for when `start_mode` is 'voice'. */
  go_phrase?: string | null;
  /** Per-tenant time display (TIMEZONE-DISPLAY): null = each viewer's local
   *  browser zone (default); an IANA zone name (e.g. "UTC") renders all of this
   *  tenant's times in that zone. Data is always stored UTC — display only. */
  display_timezone: string | null;
  stripe_subscription_status: string | null;
  created_at: string;
  deleted_at: string | null;
  /**
   * Trial-period expiry. Null = no trial (the common case). When
   * set, the backend's lazy sweep flips `is_active` to false once
   * the timestamp passes — see api/app/services/tenants.py.
   */
  trial_ends_at: string | null;
  /**
   * Populated by `GET /api/v1/tenants` (list) for superadmin
   * dashboard rendering; 0 on responses from the create / update /
   * get-by-id endpoints. See api/app/schemas/tenant.py TenantOut.
   */
  participant_count: number;
  /** Signed-in-at-least-once AND not suspended. (NOT merely "not
   *  suspended" — a manually-added participant is active-membership from
   *  creation but stays *pending* until they onboard.) */
  active_participant_count: number;
  /** Added but never signed in yet ("Invited"), not suspended.
   *  active + pending + suspended = participant_count. */
  pending_participant_count: number;
  /**
   * Same population convention as the participant counts — list-
   * only. Render as a per-tenant card breakdown on the superadmin
   * Dashboard.
   */
  procedure_count: number;
  device_count: number;
  device_active_count: number;
  session_count: number;
  /** In-service edge appliances and their cameras (retired excluded).
   *  Optional: absent from an older API that predates the counts. */
  appliance_count?: number;
  camera_count?: number;
  /**
   * Active tenant_admin emails. Populated only by the GET-by-id
   * endpoint (the list endpoint leaves this empty). Surfaced on the
   * superadmin's edit dialog as a read-only field.
   */
  tenant_admin_emails: string[];
}

/**
 * Acceptable values for the optional trial-period dropdown on the
 * Create Tenant form. Mirrors the backend's strict allow-list.
 */
export type TrialPeriodDays = 7 | 14 | 21;

// Body fragments — every contact field optional and empty-string clears
// the field server-side.
export type TenantContactPatch = Partial<TenantContact>;

export async function listTenants(includeDeleted = false): Promise<Tenant[]> {
  const { data } = await apiClient.get<Tenant[]>('/api/v1/tenants', {
    params: { include_deleted: includeDeleted },
  });
  return data;
}

export async function getTenant(id: string): Promise<Tenant> {
  const { data } = await apiClient.get<Tenant>(`/api/v1/tenants/${id}`);
  return data;
}

export async function createTenant(
  body: {
    slug: string;
    name: string;
    billing_mode: BillingMode;
    initial_admin_email?: string;
    trial_period_days?: TrialPeriodDays;
    direct_participant_entry?: boolean;
  } & TenantContactPatch,
): Promise<Tenant> {
  const { data } = await apiClient.post<Tenant>('/api/v1/tenants', body);
  return data;
}

export async function updateTenant(
  id: string,
  body: {
    name?: string;
    billing_mode?: BillingMode;
    is_active?: boolean;
    mfa_required?: boolean;
    direct_participant_entry?: boolean;
    min_eeg_rate_pct?: number;
    min_imu_rate_pct?: number;
    scorecard_hidden_metrics?: string[];
    compare_enabled_tenant?: boolean;
    compare_enabled_participant?: boolean;
    ai_coach_use_debrief?: boolean;
    session_roles_enabled?: boolean;
    start_mode?: StartMode;
    // "" clears the phrase.
    go_phrase?: string;
    // "" = viewer-local (server maps to NULL); "UTC" or an IANA zone otherwise.
    display_timezone?: string | null;
    // Superadmin-only: invite the tenant's first admin from the edit dialog
    // when it has none (backend 403s tenant_admin, 409s if one exists).
    initial_admin_email?: string;
  } & TenantContactPatch,
): Promise<Tenant> {
  const { data } = await apiClient.patch<Tenant>(`/api/v1/tenants/${id}`, body);
  return data;
}

export async function deleteTenant(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${id}`);
}

// ----------------------------------------------------------------------
// Participants of a tenant.
// ----------------------------------------------------------------------

export interface Participant {
  id: string;
  /** Empty string for an email-free participant; see `has_email`. */
  email: string;
  /**
   * False = enrolled without an email (direct-add demo flow). The
   * synthetic placeholder address is never sent down — render "no
   * email" instead of `email`.
   */
  has_email: boolean;
  full_name: string | null;
  // ROADMAP #38: cohort context surfaced alongside identity so the
  // tenant_admin Participants table can show it as columns. Both
  // nullable — portal-invited participants leave them blank until
  // they hit Profile themselves.
  title: string | null;
  years_of_experience: number | null;
  /** NPI, when set (direct-add / edit NPI lookup). Null for most. */
  npi: string | null;
  email_verified_at: string | null;
  /** Onboarding state — true once the participant has logged in at least
   *  once. False = emailed a set-password link but not signed in yet
   *  ("Invited"), or email-free (can't sign in). */
  has_signed_in: boolean;
  /** When they last signed in, or null if never. `has_signed_in` is this
   *  collapsed to a boolean; the timestamp answers the question the chip
   *  cannot — here this morning, or not since June. Account-wide, so a
   *  person in several tenants shows the same value in each. */
  last_login_at: string | null;
  is_active: boolean;
  /** Membership.created_at — when the participant joined this tenant. */
  joined_at: string;
  /** Enrolled on their own phone from the booth kiosk (BOOTH-SELF-ENROL). */
  self_enrolled?: boolean;
  /** An existing participant with the same email or name — for staff to check. */
  possible_duplicate_of?: string | null;
  /** The email typed at self-enrollment when another account already had it
   *  (so it isn't this account's email) — shown with the duplicate warning. */
  contact_email?: string | null;
  /** "Allow sign-in" — off for self-enrolled participants until staff allow it. */
  sign_in_allowed?: boolean;
}

export async function listParticipants(tenantId: string): Promise<Participant[]> {
  const { data } = await apiClient.get<Participant[]>(
    `/api/v1/tenants/${tenantId}/participants`,
  );
  return data;
}

/**
 * Direct-add a participant (email-bypass mode). Only valid when the
 * tenant's `direct_participant_entry` flag is on. `email` is optional;
 * omit it for a demo subject who never logs in. `send_welcome` mails a
 * set-password link, and is only meaningful when a real email is given.
 */
export async function createParticipantDirect(
  tenantId: string,
  body: {
    full_name: string;
    title?: string | null;
    years_of_experience?: number | null;
    email?: string | null;
    send_welcome?: boolean;
    // Optional, normally pre-filled from the NPI Registry lookup on the
    // direct-add form; persisted onto the participant.
    npi?: string | null;
    phone?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state_region?: string | null;
    postal_code?: string | null;
    country?: string | null;
  },
): Promise<Participant> {
  const { data } = await apiClient.post<Participant>(
    `/api/v1/tenants/${tenantId}/participants`,
    body,
  );
  return data;
}

export interface BulkParticipantResult {
  row: number;
  full_name: string;
  email: string | null;
  /** created · already_member · same_name · duplicate · invalid · license_limit */
  status: string;
  detail: string | null;
}

/**
 * Bulk form of the direct add (direct-entry mode): each row needs only a
 * name. `send_welcome` mails a set-password link to each NEW participant
 * who has a real email.
 */
export async function bulkCreateParticipants(
  tenantId: string,
  rows: {
    full_name: string;
    email?: string;
    title?: string;
    years_of_experience?: number;
  }[],
  sendWelcome: boolean,
): Promise<{ created: number; results: BulkParticipantResult[] }> {
  const { data } = await apiClient.post<{
    created: number;
    results: BulkParticipantResult[];
  }>(`/api/v1/tenants/${tenantId}/participants/bulk`, {
    rows,
    send_welcome: sendWelcome,
  });
  return data;
}

export async function updateParticipant(
  tenantId: string,
  userId: string,
  body: {
    full_name?: string | null;
    is_active?: boolean;
    title?: string | null;
    years_of_experience?: number | null;
    email?: string | null;
    // Only meaningful alongside an email change: mail the participant a
    // set-password ("welcome") link so they can sign in.
    send_welcome?: boolean;
    // "Allow sign-in": on mails the set-password invitation to someone who has
    // never signed in; off refuses sign-in, password reset and invitation links.
    sign_in_allowed?: boolean;
  },
): Promise<Participant> {
  const { data } = await apiClient.patch<Participant>(
    `/api/v1/tenants/${tenantId}/participants/${userId}`,
    body,
  );
  return data;
}

/** (Re)send the set-password / portal-access link to a participant who
 *  already has a real email. Works any time — independent of the
 *  create/edit forms (e.g. staff decided later, or the 1-hour link
 *  expired). 409 if the participant has no real email. */
export async function resendParticipantWelcome(
  tenantId: string,
  userId: string,
): Promise<{ sent: boolean; email: string }> {
  const { data } = await apiClient.post<{ sent: boolean; email: string }>(
    `/api/v1/tenants/${tenantId}/participants/${userId}/resend-welcome`,
  );
  return data;
}

export async function removeParticipant(
  tenantId: string,
  userId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/participants/${userId}`);
}

/**
 * One row on the per-tenant Admins page. Mirrors the shape of
 * Participant + PlatformAdmin so the table can reuse the same
 * status-chip + switch pattern.
 */
export interface TenantAdmin {
  id: string;
  email: string;
  full_name: string | null;
  email_verified_at: string | null;
  /** Membership.created_at — when this member joined the tenant. */
  joined_at: string;
  is_active: boolean;
  /** tenant_admin or coordinator (TENANT-VIEWER-ROLE). The read-only
   * toggle only applies to admins. */
  role: Role;
  /** True = read-only admin: sees the data, can't write. Always false
   * for coordinators (no read-only variant). */
  read_only: boolean;
  /** When this staff member last signed in, or null if never. Account-wide,
   *  not per-tenant — `last_login_at` lives on the user. */
  last_login_at: string | null;
  /** The same signal as a boolean, matching Participant.has_signed_in so
   *  both rosters render "Invited" from one shape. */
  has_signed_in: boolean;
}

export async function listTenantAdmins(
  tenantId: string,
): Promise<TenantAdmin[]> {
  const { data } = await apiClient.get<TenantAdmin[]>(
    `/api/v1/tenants/${tenantId}/admins`,
  );
  return data;
}

export async function updateTenantAdmin(
  tenantId: string,
  userId: string,
  body: {
    is_active?: boolean;
    read_only?: boolean;
    /** Switch a staffer between tenant_admin and coordinator. */
    role?: 'tenant_admin' | 'coordinator';
  },
): Promise<TenantAdmin> {
  const { data } = await apiClient.patch<TenantAdmin>(
    `/api/v1/tenants/${tenantId}/admins/${userId}`,
    body,
  );
  return data;
}
